import { useEffect } from 'react';

/**
 * useFocusedCell(cell, active, presence, options)
 * ------------------------------------------------------------------
 * Announces one cell as focused for as long as `active` is true, and releases
 * it on close/unmount. Every open path (toggle, outside click, scroll-away,
 * submit, navigating off the page) funnels through the same `active` flag, so
 * there is one place that can leak a held cell instead of one per call site.
 *
 * Use it for editors that live in the GRID ROW - order status dropdowns, the
 * notes popup - so other people see the activity whether or not they have the
 * order expanded.
 *
 * @param {string|null} cell      cell key from cellKey(), or null when nothing is open
 * @param {boolean}     active    whether we currently have it open
 * @param {object}      presence  the object returned by usePresence
 * @param {object}      opts      { lock: boolean } - true takes an advisory lock
 */
export default function useFocusedCell(cell, active, presence, { lock = true } = {}) {
    const { reportFocus, reportBlur } = presence || {};

    useEffect(() => {
        if (!cell || !active || !reportFocus) return undefined;
        reportFocus(cell, { lock });
        return () => reportBlur(cell);
    }, [cell, active, lock, reportFocus, reportBlur]);
}
